import type { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { Surface } from './Surface';

/**
 * EmptyState — the "you haven't created anything yet" panel.
 *
 * Wraps Surface with the brand's first-run treatment: oversized icon,
 * display headline, prose, and a slot for a primary CTA below. Used by
 * Dashboard first-run, Pages empty, Domains empty, AdminBotResearch
 * no-results, etc.
 */
export function EmptyState({
  icon = <Sparkles size={36} className="text-[var(--color-primary)]" />,
  title,
  description,
  cta,
  className = '',
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  cta?: ReactNode;
  className?: string;
}) {
  return (
    <Surface
      tone="raised"
      className={`relative overflow-hidden p-10 text-center ${className}`}
    >
      {/* Soft primary glow bleeding from top — gives the empty state
          its own quiet visual interest without a CTA-y feel. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-40"
        style={{
          background:
            'radial-gradient(ellipse at center top, rgba(124, 77, 255, 0.25), transparent 70%)',
        }}
      />
      <div className="relative z-10 flex flex-col items-center">
        <div className="flex items-center justify-center">{icon}</div>
        <h2 className="mt-5 font-display text-2xl font-bold tracking-tight">
          {title}
        </h2>
        {description && (
          <p className="mx-auto mt-3 max-w-md text-[var(--color-on-surface-variant)]">
            {description}
          </p>
        )}
        {cta && <div className="mt-7">{cta}</div>}
      </div>
    </Surface>
  );
}
